import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  type AutocompleteProps,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

const POPPER_BELOW_PROPS = {
  placement: 'bottom-start' as const,
  modifiers: [
    { name: 'flip', enabled: false },
    {
      name: 'preventOverflow',
      enabled: true,
      options: { altAxis: true, tether: false },
    },
  ],
};

/** Props para forzar el desplegable debajo en Autocomplete sueltos. */
export const searchableSelectAutocompleteProps = {
  componentsProps: { popper: POPPER_BELOW_PROPS },
} as const;

export const searchableSelectClearButtonSx: SxProps<Theme> = {
  p: 0.5,
  color: 'error.main',
  bgcolor: 'error.50',
  borderRadius: 1,
  '&:hover': {
    bgcolor: 'error.100',
  },
};

export const searchableSelectClearButtonClass = 'eone-select-clear-btn';

/** Oculta la X de limpiar y la muestra al pasar el mouse o enfocar el campo. */
export const searchableSelectClearOnHoverSx: SxProps<Theme> = {
  [`& .${searchableSelectClearButtonClass}`]: {
    visibility: 'hidden',
  },
  [`& .MuiOutlinedInput-root:hover .${searchableSelectClearButtonClass}, & .MuiOutlinedInput-root.Mui-focused .${searchableSelectClearButtonClass}, & .MuiOutlinedInput-root:focus-within .${searchableSelectClearButtonClass}`]:
    {
      visibility: 'visible',
    },
};

export const searchableSelectAddButtonSx: SxProps<Theme> = {
  p: 0.5,
  color: 'success.main',
  bgcolor: 'success.50',
  borderRadius: 1,
  '&:hover': {
    bgcolor: 'success.100',
  },
};

export const searchableSelectPopupIndicatorSx: SxProps<Theme> = {
  color: 'primary.main',
};

const insertExtraBeforePopup = (
  defaultEndAdornment: React.ReactNode,
  extra: React.ReactNode | undefined,
): React.ReactNode => {
  if (!extra) return defaultEndAdornment;
  if (!defaultEndAdornment) return extra;

  const children = React.Children.toArray(
    React.isValidElement(defaultEndAdornment) &&
      defaultEndAdornment.props &&
      typeof defaultEndAdornment.props === 'object' &&
      'children' in defaultEndAdornment.props
      ? (defaultEndAdornment.props as { children: React.ReactNode }).children
      : defaultEndAdornment,
  );

  if (children.length === 0) {
    return (
      <>
        {extra}
        {defaultEndAdornment}
      </>
    );
  }

  if (children.length === 1) {
    return (
      <>
        {extra}
        {children[0]}
      </>
    );
  }

  return (
    <>
      {children[0]}
      {extra}
      {children.slice(1)}
    </>
  );
};

const getExtraEndAdornmentAutocompleteSx = (
  hasValue: boolean,
): SxProps<Theme> => ({
  '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
    pr: '6px !important',
  },
  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
    pr: hasValue ? '92px !important' : '60px !important',
  },
  '& .MuiAutocomplete-endAdornment, & .MuiOutlinedInput-endAdornment': {
    position: 'absolute !important' as 'absolute',
    right: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    m: 0,
    height: 'auto',
    maxHeight: 'none',
  },
});

const composeEndAdornment = (
  defaultEndAdornment: React.ReactNode,
  extra: React.ReactNode | undefined,
  loading: boolean,
): React.ReactNode => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '2px',
    }}
  >
    {loading ? <CircularProgress color="inherit" size={20} /> : null}
    {insertExtraBeforePopup(defaultEndAdornment, extra)}
  </Box>
);

export interface SelectOption<T extends string | number = string | number> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface SearchableSelectBaseProps {
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: string;
  margin?: 'none' | 'dense' | 'normal';
  loading?: boolean;
  sx?: SxProps<Theme>;
  name?: string;
  allowEmpty?: boolean;
  emptyOptionLabel?: string;
  clearOnEscape?: boolean;
  openOnFocus?: boolean;
  noOptionsText?: string;
  /** Ícono o botón adicional dentro del input (p. ej. acción rápida junto al desplegable). */
  extraEndAdornment?: React.ReactNode;
}

const normalizeText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export const filterOptionsByLabel = <T extends string | number>(
  options: SelectOption<T>[],
  inputValue: string,
): SelectOption<T>[] => {
  const term = normalizeText(inputValue);
  if (!term) return options;
  return options.filter((option) => normalizeText(option.label).includes(term));
};

export const filterOptionsByLabelFn = <TOption,>(
  options: TOption[],
  inputValue: string,
  getOptionLabel: (option: TOption) => string,
): TOption[] => {
  const term = normalizeText(inputValue);
  if (!term) return options;
  return options.filter((option) => normalizeText(getOptionLabel(option)).includes(term));
};

export interface SearchableSelectProps<T extends string | number = string | number>
  extends SearchableSelectBaseProps {
  options: SelectOption<T>[];
  value: T | null | undefined;
  onChange: (value: T | null) => void;
  filterOptions?: (options: SelectOption<T>[], inputValue: string) => SelectOption<T>[];
  renderOption?: (option: SelectOption<T>) => React.ReactNode;
}

export function SearchableSelect<T extends string | number = string | number>({
  options,
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  required = false,
  fullWidth = true,
  size = 'medium',
  error = false,
  helperText,
  margin = 'none',
  loading = false,
  sx,
  name,
  allowEmpty = false,
  emptyOptionLabel = 'Seleccionar',
  clearOnEscape = true,
  openOnFocus = true,
  noOptionsText = 'Sin resultados',
  filterOptions = filterOptionsByLabel,
  renderOption,
  extraEndAdornment,
}: SearchableSelectProps<T>) {
  const selectedOption = useMemo(() => {
    if (value == null || value === ('' as T)) return null;
    return options.find((option) => option.value === value) ?? null;
  }, [options, value]);

  const resolvedPlaceholder =
    placeholder ?? (allowEmpty ? emptyOptionLabel : 'Buscar...');

  const hasExtraActions = Boolean(extraEndAdornment);
  const showClearButton = allowEmpty && !required && selectedOption != null;
  const showStyledClearButton = hasExtraActions && showClearButton;

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || loading}
      loading={loading}
      openOnFocus={openOnFocus}
      clearOnEscape={clearOnEscape}
      disableClearable={!allowEmpty || required}
      options={options}
      value={selectedOption}
      onChange={(_, option) => onChange(option?.value ?? null)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.value === current.value}
      filterOptions={(opts, state) => filterOptions(opts, state.inputValue)}
      getOptionDisabled={(option) => Boolean(option.disabled)}
      noOptionsText={noOptionsText}
      sx={hasExtraActions ? getExtraEndAdornmentAutocompleteSx(showClearButton) : undefined}
      componentsProps={{
        popper: POPPER_BELOW_PROPS,
        popupIndicator: { sx: searchableSelectPopupIndicatorSx },
        ...(showStyledClearButton
          ? { clearIndicator: { sx: searchableSelectClearButtonSx } }
          : {}),
      }}
      renderOption={
        renderOption
          ? (props, option) => (
              <li {...props} key={String(option.value)}>
                {renderOption(option)}
              </li>
            )
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          placeholder={resolvedPlaceholder}
          required={required}
          error={error}
          helperText={helperText}
          margin={margin}
          sx={sx}
          InputProps={{
            ...params.InputProps,
            endAdornment: hasExtraActions
              ? composeEndAdornment(
                  params.InputProps.endAdornment,
                  extraEndAdornment,
                  loading,
                )
              : (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
          }}
        />
      )}
    />
  );
}

export interface SearchableObjectSelectProps<TOption, TValue> extends SearchableSelectBaseProps {
  options: TOption[];
  value: TValue | null | undefined;
  onChange: (value: TValue | null, option: TOption | null) => void;
  getOptionValue: (option: TOption) => TValue;
  getOptionLabel: (option: TOption) => string;
  isOptionEqualToValue?: (option: TOption, value: TValue) => boolean;
  filterOptions?: (options: TOption[], inputValue: string) => TOption[];
  renderOption?: (option: TOption) => React.ReactNode;
  autocompleteProps?: Partial<
    AutocompleteProps<TOption, false, boolean | undefined, false>
  >;
}

export function SearchableObjectSelect<TOption, TValue>({
  options,
  value,
  onChange,
  getOptionValue,
  getOptionLabel,
  isOptionEqualToValue,
  label,
  placeholder = 'Buscar...',
  disabled = false,
  required = false,
  fullWidth = true,
  size = 'medium',
  error = false,
  helperText,
  margin = 'none',
  loading = false,
  sx,
  name,
  allowEmpty = false,
  emptyOptionLabel = 'Seleccionar',
  clearOnEscape = true,
  openOnFocus = true,
  noOptionsText = 'Sin resultados',
  filterOptions,
  renderOption,
  autocompleteProps,
}: SearchableObjectSelectProps<TOption, TValue>) {
  const compare = isOptionEqualToValue ?? ((option, current) => getOptionValue(option) === current);

  const selectedOption = useMemo(() => {
    if (value == null) return null;
    return options.find((option) => compare(option, value)) ?? null;
  }, [options, value, compare]);

  const resolvedFilter =
    filterOptions ??
    ((opts: TOption[], inputValue: string) =>
      filterOptionsByLabelFn(opts, inputValue, getOptionLabel));

  return (
    <Autocomplete
      {...autocompleteProps}
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || loading}
      loading={loading}
      openOnFocus={openOnFocus}
      clearOnEscape={clearOnEscape}
      disableClearable={!allowEmpty || required}
      options={options}
      value={selectedOption}
      onChange={(_, option) =>
        onChange(option ? getOptionValue(option) : null, option)
      }
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, current) =>
        getOptionValue(option) === getOptionValue(current)
      }
      filterOptions={(opts, state) => resolvedFilter(opts, state.inputValue)}
      noOptionsText={noOptionsText}
      componentsProps={{
        popper: POPPER_BELOW_PROPS,
        popupIndicator: { sx: searchableSelectPopupIndicatorSx },
      }}
      renderOption={
        renderOption
          ? (props, option) => (
              <li {...props} key={String(getOptionValue(option))}>
                {renderOption(option)}
              </li>
            )
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          placeholder={placeholder ?? (allowEmpty ? emptyOptionLabel : 'Buscar...')}
          required={required}
          error={error}
          helperText={helperText}
          margin={margin}
          sx={sx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export interface SearchableMultiSelectProps<T extends string | number = string>
  extends Omit<SearchableSelectBaseProps, 'allowEmpty' | 'emptyOptionLabel'> {
  options: SelectOption<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  filterOptions?: (options: SelectOption<T>[], inputValue: string) => SelectOption<T>[];
}

export function SearchableMultiSelect<T extends string | number = string>({
  options,
  value,
  onChange,
  label,
  placeholder = 'Buscar...',
  disabled = false,
  required = false,
  fullWidth = true,
  size = 'medium',
  error = false,
  helperText,
  margin = 'none',
  loading = false,
  sx,
  name,
  clearOnEscape = true,
  openOnFocus = true,
  noOptionsText = 'Sin resultados',
  filterOptions = filterOptionsByLabel,
}: SearchableMultiSelectProps<T>) {
  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value],
  );

  return (
    <Autocomplete
      multiple
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || loading}
      loading={loading}
      openOnFocus={openOnFocus}
      clearOnEscape={clearOnEscape}
      disableCloseOnSelect
      options={options}
      value={selectedOptions}
      onChange={(_, selected) => onChange(selected.map((option) => option.value))}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.value === current.value}
      filterOptions={(opts, state) => filterOptions(opts, state.inputValue)}
      getOptionDisabled={(option) => Boolean(option.disabled)}
      noOptionsText={noOptionsText}
      componentsProps={{
        popper: POPPER_BELOW_PROPS,
        popupIndicator: { sx: searchableSelectPopupIndicatorSx },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          margin={margin}
          sx={sx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default SearchableSelect;
